import { useStore } from 'zustand';
import { createStore } from 'zustand/vanilla';
import { APP_BASE } from './base';

/** What a build knows about itself; `built` identifies the build, the rest is for display. */
export interface BuildInfo {
  version: string;
  commit: string;
  built: string;
}

/** This build, stamped by vite.config.ts. */
export const BUILD: BuildInfo = __BUILD_INFO__;

export interface UpdateState {
  /** A newer build on the server, once one has been seen. */
  available: BuildInfo | null;
  checking: boolean;
  /** When the server was last asked (ms since epoch), 0 = never. */
  checkedAt: number;
  /** Last check's failure, e.g. offline. */
  error: string | null;
  /** The user dismissed the notice for this build (it stays in the About panel). */
  dismissed: boolean;
}

export const updateStore = createStore<UpdateState>(() => ({
  available: null,
  checking: false,
  checkedAt: 0,
  error: null,
  dismissed: false,
}));

export function useUpdate<T>(selector: (s: UpdateState) => T): T {
  return useStore(updateStore, selector);
}

/**
 * Asks the server which build it serves (version.json, written next to index.html by the
 * build). Returns the newer build, or null when this one is current. Dev builds never check:
 * Vite serves no version.json and the code is always the latest anyway.
 */
export async function checkForUpdate(): Promise<BuildInfo | null> {
  if (!import.meta.env.PROD) return null;
  updateStore.setState({ checking: true });
  try {
    const res = await fetch(`${APP_BASE}version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const info = (await res.json()) as Partial<BuildInfo>;
    if (typeof info.built !== 'string') throw new Error('bad version.json');
    const newer = info.built !== BUILD.built ? (info as BuildInfo) : null;
    updateStore.setState((s) => ({
      available: newer,
      dismissed: newer && s.available?.built === newer.built ? s.dismissed : false,
      error: null,
    }));
    return newer;
  } catch (e) {
    updateStore.setState({ error: e instanceof Error ? e.message : String(e) });
    return null;
  } finally {
    updateStore.setState({ checking: false, checkedAt: Date.now() });
  }
}

/**
 * Drops the service worker and every cache, then reloads: the next load fetches index.html
 * and its assets from the server. The hash route survives, so the same board reopens.
 */
export async function hardRefresh(): Promise<void> {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* reload anyway */
  }
  window.location.reload();
}

const RELOADED_KEY = 'vds:reloaded-for';
/** The tab was away at least this long before an automatic reload on return is acceptable. */
const AWAY_MS = 5 * 60_000;
const VISIBLE_THROTTLE_MS = 60_000;
const INTERVAL_MS = 15 * 60_000;

/**
 * Keeps the running app close to what the server serves. A newer build found right at start-up
 * (the shell came from a cache) reloads immediately, once per build so a bad deploy cannot
 * loop. Coming back to the tab after a long absence does the same. While the app is in use
 * the newer build is only announced (UpdateNotice) so nothing is interrupted mid-edit.
 */
export function startUpdateChecks(): void {
  if (!import.meta.env.PROD) return;
  const reloadOnce = (info: BuildInfo) => {
    let done: string | null = null;
    try {
      done = sessionStorage.getItem(RELOADED_KEY);
      sessionStorage.setItem(RELOADED_KEY, info.built);
    } catch {
      /* no sessionStorage: still reload, the guard just does not apply */
    }
    if (done !== info.built) void hardRefresh();
  };

  void checkForUpdate().then((newer) => {
    if (newer) reloadOnce(newer);
  });

  let hiddenAt = 0;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now();
      return;
    }
    const away = hiddenAt ? Date.now() - hiddenAt : 0;
    if (Date.now() - updateStore.getState().checkedAt < VISIBLE_THROTTLE_MS) return;
    void checkForUpdate().then((newer) => {
      if (newer && away >= AWAY_MS) reloadOnce(newer);
    });
  });

  setInterval(() => {
    if (document.visibilityState === 'visible') void checkForUpdate();
  }, INTERVAL_MS);
}

/** "11 Sep 2026, 14:03" for the About panel; the raw stamp when it does not parse. */
export function formatBuilt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
